import { UserCircleIcon } from '@heroicons/react/20/solid';

import useAuth from '@/hooks/useAuth';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

import { NoShowShift } from '../api/types';
import { formatMarkedAt, formatShiftDateTime } from '../util/dateFormat';

import Avatar from './Avatar';

import { cardActivation } from '../util/cardActivation';

// One no-show card = a shift header (Client · Date & time · Skill · Location)
// with a drill-in chevron, and the worker rows beneath it (a shift can carry
// several).
//
// ## Layout: ONE grid definition, shared (GRAVY-10317)
//
// The header and the worker rows sized themselves independently — the header on
// flex bases 432/220/200/140, the rows on 363/190/grow — so a status, marker or
// timestamp did not sit under the column heading it belongs to. Both now use
// `dashv2-nsgrid`, a single `grid-template-columns` they share, which makes the
// alignment structural rather than two sets of numbers kept in step by hand:
//
//   Client        -> worker avatar + name
//   Date & time   -> Status
//   Skill         -> Marked by
//   Location      -> Date & time marked
//   (action)      -> Call worker
//
// It is scoped to this card rather than applied to `.dashv2-nshead`, which
// Missing Timesheet also uses — its rows are finer-grained than its header (four
// timesheet columns under two headings), so it needs its own track list and
// spanning header cells (`dashv2-tsgrid`).
//
// ## The chevron is a drill-in, not a toggle
//
// Worker rows are ALWAYS rendered — the approved behaviour is expanded, and there
// is no collapse. The right-facing chevron opens this exact shift in the shared
// Shift Detail panel, matching the Needs Approval row and the Missing Timesheet
// chevron (GRAVY-10318). It used to rotate downward and collapse the rows,
// which spent the card's only affordance on hiding its own content.
//
// ## Status vocabulary
//
// The queue carries two populations: CONFIRMED absences, and unresolved
// attendance alerts surfaced early. Every row used to read "No show", which made
// a supervisor who had merely not answered an SMS look like a worker who had
// definitely failed to turn up (GRAVY-10302). Each row now shows its real state,
// and an automated alert is attributed to the presence check rather than to the
// supervisor it happened to text.
const STATUS_LABEL: Record<string, string> = {
  no_show: 'No show',
  awaiting_confirmation: 'Awaiting confirmation',
  backfill_requested: 'Backfill requested',
  escalated: 'Escalated',
};

type Props = {
  shift: NoShowShift;
  /** Opens the shared Shift Detail panel for this exact occurrence. */
  onSelect?: (shiftId: number) => void;
};

const NoShowShiftCard = ({ shift, onSelect }: Props) => {
  const { currentAdmin, isCurrentCustomerAdmin } = useAuth();
  // Guarded on null — `isCurrentCustomerAdmin` reads `.customer` and throws
  // while the session resolves. Defaulting to NOT tenant-admin is the safe
  // direction: the shortcut stays hidden until the role is known.
  const tenantAdmin = !!currentAdmin && !isCurrentCustomerAdmin(currentAdmin);

  const tz = shift.timezone;

  const col = (label: string, value: string, modifier: string) => (
    <div className={`dashv2-nscol dashv2-nscol--${modifier}`}>
      <p className="dashv2-nslabel">{label}</p>
      <p className="dashv2-nsvalue" title={value}>
        {value || '—'}
      </p>
    </div>
  );

  return (
    <div
      className="dashv2-nscard"
      {...cardActivation(onSelect ? () => onSelect(shift.shift_id) : undefined)}
    >
      <div className="dashv2-nshead dashv2-nsgrid">
        {col('Client', shift.client_name || '', 'client')}
        {col(
          'Date & time',
          formatShiftDateTime(shift.start_at, shift.end_at, tz),
          'date',
        )}
        {col('Skill', shift.skill_name || '', 'skill')}
        {col('Location', shift.location_code || '', 'loc')}
        <button
          type="button"
          className="dashv2-nsdrill"
          id={`dashv2-noshow-chevron-${shift.shift_id}`}
          aria-label="Open shift detail"
          disabled={!onSelect}
          onClick={(e) => {
            // Focus explicitly before the panel mounts: the panel hands focus
            // back to whatever was active when it opened, and Safari does not
            // focus a button on click.
            e.currentTarget.focus();
            onSelect?.(shift.shift_id);
          }}
        >
          <ChevronRightIcon />
        </button>
      </div>

      <div className="dashv2-nsworkers">
        {shift.workers.map((w) => (
          <div className="dashv2-nsrow dashv2-nsgrid" key={w.id}>
            <div className="dashv2-nsuser">
              <Avatar name={w.name} src={w.avatar_url} />
              <span className="dashv2-nsname">
                {w.name || 'Unknown worker'}
              </span>
            </div>
            <div className="dashv2-nsfield">
              <p className="dashv2-nslabel">Status</p>
              <span className={`dashv2-nsbadge state-${w.status}`}>
                {STATUS_LABEL[w.status] ?? STATUS_LABEL.no_show}
              </span>
            </div>
            <div className="dashv2-nsfield">
              <p className="dashv2-nslabel">Marked by</p>
              <p className="dashv2-nsfield-val">
                {w.marked_automatically
                  ? 'Flagged automatically'
                  : w.marked_by || '—'}
              </p>
            </div>
            <div className="dashv2-nsfield">
              <p className="dashv2-nslabel">Date &amp; time marked</p>
              <p className="dashv2-nsfield-val">
                {formatMarkedAt(w.marked_at, tz)}
              </p>
            </div>
            {/* Call the worker who did not turn up, matching the Missing
                Timesheet action. The cell is always rendered so a worker with no
                authorized number does not pull the other columns out of
                alignment — it simply holds nothing. */}
            <div className="dashv2-nsaction">
              {/* Worker Profile, replacing the call action (GRAVY-10333).
                  Ops need the worker's context and history from a queue, not a
                  phone dialer.

                  TENANT ADMIN ONLY, and gated by RENDERING — not by hiding.
                  A Client Admin gets no element at all, so there is nothing to
                  reveal through keyboard focus or the DOM. Server-side profile
                  authorization still applies; this is the UI half only. */}
              {tenantAdmin && (
                <Link
                  className="dashv2-nsprofile"
                  to={`/workers_v2/${w.worker_id}`}
                  aria-label={`View worker profile for ${w.name || 'worker'}`}
                  // The chevron opens Shift Detail; the profile must never also
                  // trigger it should the row become clickable.
                  onClick={(e) => e.stopPropagation()}
                >
                  <UserCircleIcon />
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NoShowShiftCard;
